export type AppVersionManifest = {
      version: string;
      minimumSupportedVersion: string | null;
      gitSha: string | null;
      generatedAt: string;
};

export type AppVersionStatus = "current" | "update-available" | "update-required";

export type AppVersionState = {
      currentVersion: string;
      latestVersion: string;
      minimumSupportedVersion: string | null;
      status: AppVersionStatus;
};

export const APP_VERSION = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "test-build";
export const APP_MINIMUM_SUPPORTED_VERSION =
      typeof __APP_MINIMUM_SUPPORTED_VERSION__ === "string" ? __APP_MINIMUM_SUPPORTED_VERSION__ : null;
export const APP_VERSION_MANIFEST_PATH = "/version.json";
export const APP_VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

const isRecord = (value: unknown): value is Record<string, unknown> =>
      typeof value === "object" && value !== null;

const normalizeOptionalString = (value: unknown): string | null => {
      if (typeof value !== "string") {
            return null;
      }

      const normalizedValue = value.trim();
      return normalizedValue.length > 0 ? normalizedValue : null;
};

const isComparableTimestamp = (value: string | null | undefined): value is string => {
      if (!value) {
            return false;
      }

      return Number.isFinite(Date.parse(value));
};

export const parseAppVersionManifest = (value: unknown): AppVersionManifest | null => {
      if (!isRecord(value)) {
            return null;
      }

      const version = normalizeOptionalString(value.version);
      const generatedAt = normalizeOptionalString(value.generatedAt);

      if (!version || !generatedAt) {
            return null;
      }

      return {
            version,
            minimumSupportedVersion: normalizeOptionalString(value.minimumSupportedVersion),
            gitSha: normalizeOptionalString(value.gitSha),
            generatedAt,
      };
};

export const compareAppVersions = (left: string, right: string): number | null => {
      if (!isComparableTimestamp(left) || !isComparableTimestamp(right)) {
            return left === right ? 0 : null;
      }

      const leftTimestamp = Date.parse(left);
      const rightTimestamp = Date.parse(right);

      if (leftTimestamp === rightTimestamp) {
            return 0;
      }

      return leftTimestamp < rightTimestamp ? -1 : 1;
};

export const resolveAppVersionState = (
      currentVersion: string,
      latestManifest: AppVersionManifest,
): AppVersionState => {
      const latestVersion = latestManifest.version;
      const minimumSupportedVersion = latestManifest.minimumSupportedVersion ?? APP_MINIMUM_SUPPORTED_VERSION;

      let status: AppVersionStatus = currentVersion === latestVersion ? "current" : "update-available";

      if (minimumSupportedVersion) {
            const minimumSupportedComparison = compareAppVersions(currentVersion, minimumSupportedVersion);

            if (minimumSupportedComparison !== null && minimumSupportedComparison < 0) {
                  status = "update-required";
            }
      }

      return {
            currentVersion,
            latestVersion,
            minimumSupportedVersion,
            status,
      };
};
